import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

/**
 * GlobalSearchModal - System-wide search modal for admin users (user_id = 1)
 * Searches across RFQs, Specifications, Requisitions, and Quotations
 * without department filtering.
 */
const GlobalSearchModal = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    rfqs: [],
    specifications: [],
    requisitions: [],
    quotations: []
  });
  const [activeTab, setActiveTab] = useState('all');
  const [error, setError] = useState(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Clear results when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setResults({ rfqs: [], specifications: [], requisitions: [], quotations: [] });
      setActiveTab('all');
      setError(null);
    }
  }, [isOpen]);

  // Debounced search
  const performSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setResults({ rfqs: [], specifications: [], requisitions: [], quotations: [] });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/global-search', {
        params: { q: term, limit: 15 }
      });

      if (response.data.status === 1) {
        setResults(response.data.data);
      } else {
        setError(response.data.message || 'Search failed');
      }
    } catch (err) {
      console.error('Global search error:', err);
      setError(err.response?.data?.message || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, performSearch]);

  // Handle result click - navigate to the item's page
  const handleResultClick = (item) => {
    if (item.url) {
      navigate(item.url);
      onClose();
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Get total count of results
  const getTotalCount = () => {
    return results.rfqs.length + 
           results.specifications.length + 
           results.requisitions.length + 
           results.quotations.length;
  };

  // Get filtered results based on active tab
  const getFilteredResults = () => {
    switch (activeTab) {
      case 'rfqs':
        return { ...results, specifications: [], requisitions: [], quotations: [] };
      case 'specifications':
        return { ...results, rfqs: [], requisitions: [], quotations: [] };
      case 'requisitions':
        return { ...results, rfqs: [], specifications: [], quotations: [] };
      case 'quotations':
        return { ...results, rfqs: [], specifications: [], requisitions: [] };
      default:
        return results;
    }
  };

  // Render a single result item
  const renderResultItem = (item, index) => {
    const getTypeIcon = () => {
      switch (item.type) {
        case 'rfq':
          return (
            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          );
        case 'specification':
          return (
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          );
        case 'requisition':
          return (
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          );
        case 'quotation':
          return (
            <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          );
        default:
          return null;
      }
    };

    const getTitle = () => {
      switch (item.type) {
        case 'rfq':
          return item.procurement_ref || item.specification_title || 'RFQ';
        case 'specification':
          return item.specification_title || 'Specification';
        case 'requisition':
          return item.requisition_number || item.requisition_title || 'Requisition';
        case 'quotation':
          return item.quotation_company_name || 'Quotation';
        default:
          return 'Item';
      }
    };

    const getSubtitle = () => {
      switch (item.type) {
        case 'rfq':
          return `${item.department_name || 'Unknown Dept'} • ${item.user_full_name || 'Unknown User'} • ${item.quotes_count || 0} quotes`;
        case 'specification':
          return `${item.department_name || 'Unknown Dept'} • ${item.user_full_name || 'Unknown User'}${item.requisition_number ? ` • Req: ${item.requisition_number}` : ''}`;
        case 'requisition':
          return `${item.department_name || 'Unknown Dept'} • ${item.user_full_name || 'Unknown User'}${item.quotation_company_name ? ` • ${item.quotation_company_name}` : ''}`;
        case 'quotation':
          return `${item.procurement_ref || 'No RFQ'} • ${item.specification_title || 'No Specification'}`;
        default:
          return '';
      }
    };

    const getTypeBadge = () => {
      const badges = {
        rfq: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'RFQ' },
        specification: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Spec' },
        requisition: { bg: 'bg-green-100', text: 'text-green-800', label: 'Req' },
        quotation: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Quote' }
      };
      const badge = badges[item.type] || { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Item' };
      return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
          {badge.label}
        </span>
      );
    };

    return (
      <button
        key={`${item.type}-${index}`}
        onClick={() => handleResultClick(item)}
        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-start gap-3"
        disabled={!item.url}
      >
        <div className="flex-shrink-0 mt-0.5">
          {getTypeIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">{getTitle()}</span>
            {getTypeBadge()}
          </div>
          <div className="text-sm text-gray-500 truncate mt-0.5">
            {getSubtitle()}
          </div>
        </div>
        {item.url && (
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        )}
      </button>
    );
  };

  if (!isOpen) return null;

  const filteredResults = getFilteredResults();
  const hasResults = getTotalCount() > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex items-start justify-center min-h-screen pt-16 px-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl transform transition-all">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900">Global Search</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Search Input */}
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search RFQs, Specifications, Requisitions, Quotations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <svg 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {loading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>

            {/* Tabs */}
            {hasResults && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All ({getTotalCount()})
                </button>
                {results.rfqs.length > 0 && (
                  <button
                    onClick={() => setActiveTab('rfqs')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'rfqs'
                        ? 'bg-orange-500 text-white'
                        : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                    }`}
                  >
                    RFQs ({results.rfqs.length})
                  </button>
                )}
                {results.specifications.length > 0 && (
                  <button
                    onClick={() => setActiveTab('specifications')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'specifications'
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    }`}
                  >
                    Specs ({results.specifications.length})
                  </button>
                )}
                {results.requisitions.length > 0 && (
                  <button
                    onClick={() => setActiveTab('requisitions')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'requisitions'
                        ? 'bg-green-500 text-white'
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                    }`}
                  >
                    Reqs ({results.requisitions.length})
                  </button>
                )}
                {results.quotations.length > 0 && (
                  <button
                    onClick={() => setActiveTab('quotations')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'quotations'
                        ? 'bg-purple-500 text-white'
                        : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                    }`}
                  >
                    Quotes ({results.quotations.length})
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {error && (
              <div className="p-4 text-center text-red-600">
                <svg className="w-8 h-8 mx-auto mb-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm">{error}</p>
              </div>
            )}

            {!error && !loading && !searchTerm && (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="font-medium">Start typing to search</p>
                <p className="text-sm mt-1">Search across all RFQs, Specifications, Requisitions, and Quotations</p>
              </div>
            )}

            {!error && !loading && searchTerm && searchTerm.length < 2 && (
              <div className="p-8 text-center text-gray-500">
                <p className="text-sm">Type at least 2 characters to search</p>
              </div>
            )}

            {!error && !loading && searchTerm && searchTerm.length >= 2 && !hasResults && (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-medium">No results found</p>
                <p className="text-sm mt-1">Try a different search term</p>
              </div>
            )}

            {!error && hasResults && (
              <div>
                {/* RFQs */}
                {filteredResults.rfqs.length > 0 && (
                  <div>
                    {activeTab === 'all' && (
                      <div className="px-4 py-2 bg-orange-50 text-xs font-semibold text-orange-800 uppercase tracking-wider">
                        RFQs ({filteredResults.rfqs.length})
                      </div>
                    )}
                    {filteredResults.rfqs.map((item, index) => renderResultItem({ ...item, type: 'rfq' }, index))}
                  </div>
                )}

                {/* Specifications */}
                {filteredResults.specifications.length > 0 && (
                  <div>
                    {activeTab === 'all' && (
                      <div className="px-4 py-2 bg-blue-50 text-xs font-semibold text-blue-800 uppercase tracking-wider">
                        Specifications ({filteredResults.specifications.length})
                      </div>
                    )}
                    {filteredResults.specifications.map((item, index) => renderResultItem({ ...item, type: 'specification' }, index))}
                  </div>
                )}

                {/* Requisitions */}
                {filteredResults.requisitions.length > 0 && (
                  <div>
                    {activeTab === 'all' && (
                      <div className="px-4 py-2 bg-green-50 text-xs font-semibold text-green-800 uppercase tracking-wider">
                        Requisitions ({filteredResults.requisitions.length})
                      </div>
                    )}
                    {filteredResults.requisitions.map((item, index) => renderResultItem({ ...item, type: 'requisition' }, index))}
                  </div>
                )}

                {/* Quotations */}
                {filteredResults.quotations.length > 0 && (
                  <div>
                    {activeTab === 'all' && (
                      <div className="px-4 py-2 bg-purple-50 text-xs font-semibold text-purple-800 uppercase tracking-wider">
                        Quotations ({filteredResults.quotations.length})
                      </div>
                    )}
                    {filteredResults.quotations.map((item, index) => renderResultItem({ ...item, type: 'quotation' }, index))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Press <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-700 font-mono">Esc</kbd> to close</span>
              <span>Global search bypasses department filters</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchModal;
